import { createSideUpdater } from "../../utils/createSideUpdater";
import {
  generateUpdatedImagePengecekanMobil,
  mergeSavedDetils,
  mergeSavedMarkingDetils,
  stripClientFlags,
} from "../../utils/editorHelpers";

const useEditorDetilApi = ({
  requestPost,
  requestDelete,
  uiContext,
  pengecekanSaved,
  setPengecekanSaved,
  pengecekanBerangkatData,
  setPengecekanBerangkatData,
  pengecekanKembaliData,
  setPengecekanKembaliData,
  pengecekanBerangkatDetilData,
  setPengecekanBerangkatDetilData,
  pengecekanKembaliDetilData,
  setPengecekanKembaliDetilData
}) => {
  // update the data / the detil rows of one side ("berangkat" or "kembali")
  const updatePengecekanData = createSideUpdater({
    berangkat: pengecekanBerangkatData,
    setBerangkat: setPengecekanBerangkatData,
    kembali: pengecekanKembaliData,
    setKembali: setPengecekanKembaliData,
  });
  const updateDetilList = createSideUpdater({
    berangkat: pengecekanBerangkatDetilData,
    setBerangkat: setPengecekanBerangkatDetilData,
    kembali: pengecekanKembaliDetilData,
    setKembali: setPengecekanKembaliDetilData,
  });

  // API call
  /**
   * Saves the pengecekan data by making a POST request to the server.
   *
   * @async
   * @function savePengecekan
   * @param {string} type - The type of pengecekan, either "berangkat" or "kembali".
   * @returns {Promise<void>} - A promise that resolves when the request is complete.
   * @throws Will show an error message if the request fails.
   */
  const savePengecekan = async (type) => {
    const body =
      type === "berangkat"
        ? { ...pengecekanBerangkatData }
        : { ...pengecekanKembaliData };
    requestPost({
      fullUrl: "/api/pemakaian/pengecekanmobil/save",
      body,
      isForm: false,
      onError: ({ message, data }) => {
        uiContext.showErrorMsg("Error", message);
      },
      onSuccess: ({ data, message }) => {
        uiContext.showSuccessMsg("Success", message);
        // reload front end data
        updatePengecekanData(type, () => data);
      },
    });
  };

  const saveCekDetil = async ({ detils, type }) => {
    const body = detils.map(stripClientFlags);
    requestPost({
      fullUrl: "/api/pemakaian/pengecekanmobildetil/savemany",
      body,
      isForm: false,
      onError: ({ message, data }) => {
        uiContext.showErrorMsg("Error", message);
      },
      onSuccess: ({ data, message }) => {
        uiContext.showSuccessMsg("Success", message);
        // reload front end data
        if (data) {
          updateDetilList(type, (list) => mergeSavedDetils(list, data));
        }
        if (detils.length > 0) {
          const newPengecekanSaved = {
            ...pengecekanSaved,
            [detils[0].label_grup]: true,
          };
          setPengecekanSaved(newPengecekanSaved);
        }
      },
    });
  };

  const saveDetil = async ({ detil, type }) => {
    const body = detil;
    const isNew = !detil.id || detil.id <= 0;
    return requestPost({
      fullUrl: "/api/pemakaian/pengecekanmobildetil/save",
      body,
      isForm: false,
      onError: ({ message, data }) => {
        uiContext.showErrorMsg("Error", message);
      },
      onSuccess: ({ data, message }) => {
        uiContext.showSuccessMsg("Success", message);
        // reload front end data
        if (data) {
          if (isNew) {
            updateDetilList(type, (list) =>
              generateUpdatedImagePengecekanMobil([...list, data])
            );
          } else {
            updateDetilList(type, (list) => mergeSavedDetils(list, data));
          }
        }
      },
    });
  };

  const deleteDetil = async ({ id, type }) => {
    requestDelete({
      fullUrl: `/api/pemakaian/pengecekanmobildetil/delete`,
      ids: [id],
      onError: ({ message, data }) => {
        uiContext.showErrorMsg("Error", message);
      },
      onSuccess: ({ data, message }) => {
        uiContext.showSuccessMsg("Success", message);
        // reload front end data
        updateDetilList(type, (list) => list.filter((detil) => detil.id !== id));
      },
    });
  };

  const saveDetilMany = async ({ type, labelGrup, pengecekanDetilData }) => {
    // _localId only stages a marking added with Beri Tanda before it is saved (see useEditorMarking.js) and _dirty
    // marks an unsaved change; the backend doesn't know them, so they're stripped from the actual request body
    const body = pengecekanDetilData.map(stripClientFlags);

    requestPost({
      fullUrl: "/api/pemakaian/pengecekanmobildetil/savemany",
      body,
      isForm: false,
      onError: ({ message, data }) => {
        uiContext.showErrorMsg("Error", message);
      },
      onSuccess: ({ data, message }) => {
        uiContext.showSuccessMsg("Success", message);
        // reload front end data
        if (data) {
          updateDetilList(type, (list) =>
            mergeSavedMarkingDetils(list, pengecekanDetilData, data)
          );
        }
      },
    });

    // console.log("saveDetilMany", type, labelGrup, pengecekanDetilData);
  };

  const saveNotePengencekanDetail = async (row) => {
    const body = {
      id: row.id,
      note: row.note,
    };
    requestPost({
      fullUrl: "/api/pemakaian/pengecekanmobildetil/update",
      body,
      isForm: false,
      onError: ({ message, data }) => {
        uiContext.showErrorMsg("Error", message);
      },
      onSuccess: ({ data, message }) => {
        uiContext.showSuccessMsg("Success", message);
        // handleOnClickReload();

        updateDetilList(row.tipe, (list) =>
          generateUpdatedImagePengecekanMobil(
            list.map((detil) => {
              if (detil.id === row.id) {
                return { ...detil, note: data.note };
              }
              return { ...detil };
            })
          )
        );
      },
    });
  };

  return {
    savePengecekan,
    saveCekDetil,
    saveDetil,
    deleteDetil,
    saveDetilMany,
    saveNotePengencekanDetail
  };
};

export default useEditorDetilApi;
